import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from '../context/AuthContext';
import dischargeService, { Discharge } from "../services/discharge.service";

const riskColors: Record<string, { bg: string; color: string; }> = {
  Low: { bg: "rgba(31, 181, 81, 0.1)", color: "#1FB551" },
  Medium: { bg: "rgba(255, 169, 77, 0.1)", color: "#FFA94D" },
  High: { bg: "rgba(255, 77, 77, 0.1)", color: "#FF4D4D" },
};

export default function DischargeDetails() {
  const { id } = useParams<{ id: string; }>();
  const navigate = useNavigate();
  const [discharge, setDischarge] = useState<Discharge | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [followupNote, setFollowupNote] = useState('');
  const [followupDate, setFollowupDate] = useState('');
  const [followupTime, setFollowupTime] = useState('');
  const [followupOutcome, setFollowupOutcome] = useState('Successful Contact');
  const [calls, setCalls] = useState<any[]>([]);
  const [followUpEnabled, setFollowUpEnabled] = useState(true);

  const fetchCalls = useCallback(async () => {
    if (!id) return;
    try {
      const list = await dischargeService.listFollowUps(id);
      setCalls(list);
    } catch (err) {
      console.error('Failed to fetch follow-up calls', err);
    }
  }, [id]);

  useEffect(() => {
    const fetchDischarge = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await dischargeService.getDischargeById(id);
        setDischarge(data);
        setFollowUpEnabled(String(data.status).toLowerCase() === 'active');
      } catch (error) {
        console.error("Failed to fetch discharge details:", error);
        toast.error("Failed to load discharge details");
      } finally {
        setLoading(false);
      }
    };

    fetchDischarge();
    fetchCalls();
  }, [id, fetchCalls]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!discharge) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          Discharge record not found
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/user-layout/patient-discharge")}
          sx={{ mt: 2 }}
        >
          Back to Discharge List
        </Button>
      </Box>
    );
  }


  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/user-layout/patient-discharge")}
          sx={{ mr: 2 }}
        >
        </Button>
        <Typography variant="h5" fontWeight="bold">
          Discharge
        </Typography>
      </Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={3}>
          <Box flex={1} minWidth="200px">
            <Typography variant="subtitle2" color="textSecondary" mb={1}>
              Primary Diagnosis
            </Typography>
            <Typography variant="body1">
              {discharge.primary_diagnosis || "Not specified"}
            </Typography>
          </Box>

          <Box flex={1} minWidth="200px">
            <Typography variant="subtitle2" color="textSecondary" mb={1}>
              Discharge Summary
            </Typography>
            <Typography variant="body1">
              {discharge.discharge_summary || "Not specified"}
            </Typography>
          </Box>

          <Box flex={0.5} minWidth="150px">
            <Typography variant="subtitle2" color="textSecondary" mb={1}>
              Allergies
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {discharge.allergies && discharge.allergies.length > 0 ? (
                discharge.allergies.map((allergy: string, idx: number) => (
                  <Chip
                    key={`allergy-${allergy}-${idx}`}
                    label={allergy}
                    size="small"
                    sx={{
                      backgroundColor: "#f5f5f5",
                      fontWeight: 500,
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2">None</Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box mt={2}>
          <Typography variant="subtitle2" color="textSecondary" mb={1}>
            Risk Level
          </Typography>
          <Chip
            label={discharge.risk_level}
            size="medium"
            sx={{
              backgroundColor: riskColors[discharge.risk_level]?.bg || "#f5f5f5",
              color: riskColors[discharge.risk_level]?.color || "#333",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          />
        </Box>
      </Paper>
      {discharge.home_care_instructions && discharge.home_care_instructions.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Home Care Instructions
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" mb={2}>
            Instructions
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {discharge.home_care_instructions.map((instruction: any, idx: number) => (
              <Box key={`instruction-${instruction.title}-${idx}`}>
                <Typography variant="body1" fontWeight="600" mb={0.5}>
                  {instruction.title}:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {instruction.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
      {discharge.lab_diagnostics && discharge.lab_diagnostics.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Lab/Diagnostic
          </Typography>
          <Box display="flex" flexDirection="column" gap={3}>
            {discharge.lab_diagnostics.map((lab: any, idx: number) => (
              <Box key={`lab-${lab.test_name}-${idx}`}>
                <Typography variant="body1" fontWeight="600" mb={1}>
                  Lab Tests - {lab.test_name}
                </Typography>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="body2">
                    <strong>Frequency -</strong> {lab.frequency}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Instructions -</strong> {lab.instructions}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Priority -</strong> {lab.priority}
                  </Typography>
                  {lab.reason && (
                    <Typography variant="body2">
                      <strong>Reason -</strong> {lab.reason}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
      {discharge.regular_followup && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Regular Follow-up
          </Typography>

          {discharge.regular_followup.followup_date && (
            <Box mb={2}>
              <Typography variant="subtitle1" color="primary" fontWeight="600" mb={1}>
                Follow-up Date & Time
              </Typography>
              <Typography variant="body1">
                {new Date(discharge.regular_followup.followup_date).toLocaleDateString('en-US', {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric'
                })}{" "}
                | {discharge.regular_followup.followup_time || "Time not specified"}
              </Typography>
            </Box>
          )}

          {discharge.regular_followup.observations && discharge.regular_followup.observations.length > 0 && (
            <Box mb={2}>
              <Typography variant="body1" fontWeight="600" mb={1}>
                Follow-up observations
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {discharge.regular_followup.observations.map((obs: string, idx: number) => (
                  <li key={`obs-${idx}-${obs.substring(0, 10)}`}>
                    <Typography variant="body2">{obs}</Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}

          {discharge.regular_followup.medications && discharge.regular_followup.medications.length > 0 && (
            <Box mb={2}>
              <Typography variant="body1" fontWeight="600" mb={1}>
                Medications
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {discharge.regular_followup.medications.map((med: string, idx: number) => (
                  <li key={`med-${idx}-${med.substring(0, 10)}`}>
                    <Typography variant="body2">{med}</Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}

          {discharge.regular_followup.lifestyle_recommendations && discharge.regular_followup.lifestyle_recommendations.length > 0 && (
            <Box>
              <Typography variant="body1" fontWeight="600" mb={1}>
                Lifestyle Recommendations
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {discharge.regular_followup.lifestyle_recommendations.map((rec: string, idx: number) => (
                  <li key={`rec-${idx}-${rec.substring(0, 10)}`}>
                    <Typography variant="body2">{rec}</Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </Paper>
      )}
      {discharge.referral && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box flex={1}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Referral
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="body2">
                  <strong>Referral Type -</strong> {discharge.referral.referral_type || "Not specified"}
                </Typography>
                <Typography variant="body2">
                  <strong>Specialty -</strong> {discharge.referral.specialty || "Not specified"}
                </Typography>
                <Typography variant="body2">
                  <strong>Location -</strong> {discharge.referral.location || "Not specified"}
                </Typography>
                <Typography variant="body2">
                  <strong>Provider Name -</strong> {discharge.referral.provider_name || "Not specified"}
                </Typography>
                <Typography variant="body2">
                  <strong>Priority -</strong> {discharge.referral.priority || "Not specified"}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              color="primary"
              sx={{
                textTransform: "none",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
              }}
              onClick={() => {
                toast.info("Schedule appointment feature coming soon!");
              }}
            >
              Schedule Appointment
            </Button>
          </Box>
        </Paper>
      )}
      {user?.role === 'PROVIDER' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              1. Initial Follow-up Call
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="textSecondary">Follow-up</Typography>
              <label
                htmlFor="followup-toggle"
                aria-label={followUpEnabled ? 'Disable follow-up' : 'Enable follow-up'}
                style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}
              >
                <span style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                  {followUpEnabled ? 'Disable follow-up' : 'Enable follow-up'}
                </span>
                <input
                  id="followup-toggle"
                  type="checkbox"
                  checked={followUpEnabled}
                  onChange={(e) => setFollowUpEnabled(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                  role="switch"
                  aria-checked={followUpEnabled}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: followUpEnabled ? '#4FD1C5' : '#ccc',
                  transition: '0.4s',
                  borderRadius: '24px'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '18px',
                    width: '18px',
                    left: followUpEnabled ? '28px' : '4px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '0.4s',
                    borderRadius: '50%'
                  }} />
                </span>
              </label>
            </Box>
          </Box>

          {followUpEnabled ? (
            <>
              <Box display="flex" gap={2} mb={2}>
                <input
                  type="date"
                  value={followupDate}
                  onChange={(e) => setFollowupDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="time"
                  value={followupTime}
                  onChange={(e) => setFollowupTime(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <select
                  value={followupOutcome}
                  onChange={(e) => setFollowupOutcome(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '180px'
                  }}
                >
                  <option>Successful Contact</option>
                  <option>Left Voicemail</option>
                  <option>No Answer</option>
                  <option>Wrong Number</option>
                  <option>Patient Unavailable</option>
                </select>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary" mb={1}>Call Notes</Typography>
                <textarea
                  rows={4}
                  placeholder="Enter detailed notes from the call..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  value={followupNote}
                  onChange={(e) => setFollowupNote(e.target.value)}
                />
              </Box>
              <Button
                variant="contained"
                onClick={async () => {
                  if (!id) return;
                  try {
                    await dischargeService.createFollowUp(id, {
                      call_date: followupDate,
                      call_time: followupTime,
                      call_outcome: followupOutcome,
                      call_notes: followupNote,
                    });
                    setFollowupNote('');
                    setFollowupDate('');
                    setFollowupTime('');
                    fetchCalls();
                    toast.success('Follow-up logged');
                  } catch (err) {
                    console.error(err);
                    toast.error('Failed to log follow-up');
                  }
                }}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  backgroundColor: '#00A8E8',
                  '&:hover': { backgroundColor: '#0096D1' }
                }}
              >
                Log Call
              </Button>

              <Box mt={3}>
                <Typography variant="subtitle1" mb={1} fontWeight="600">Call History</Typography>
                {calls.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">No calls logged yet</Typography>
                ) : (
                  calls.map((c) => (
                    <Paper key={c.id} sx={{ p: 2, mb: 1, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                      <Typography variant="body2" fontWeight="600">
                        {c.call_date} | {c.call_time} — {c.call_outcome}
                      </Typography>
                      {c.call_notes && (
                        <Typography variant="body2" color="textSecondary" mt={0.5}>
                          {c.call_notes}
                        </Typography>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
            </>
          ) : (
            <Box sx={{
              p: 3,
              backgroundColor: '#f9fafb',
              borderRadius: 2,
              border: '1px solid #e5e7eb'
            }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Discharge Summary: {discharge?.patient?.first_name} {discharge?.patient?.last_name}
              </Typography>
              <Box display="flex" gap={4} flexWrap="wrap">
                <Box>
                  <Typography variant="body2" color="textSecondary">Age</Typography>
                  <Typography variant="body1" fontWeight="600">
                    {(discharge?.patient as any)?.birth_date
                      ? `${new Date().getFullYear() - new Date((discharge.patient as any).birth_date).getFullYear()} years`
                      : 'N/A'
                    }
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">MRN</Typography>
                  <Typography variant="body1" fontWeight="600">{discharge?.id?.slice(0, 8) || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Contact Number</Typography>
                  <Typography variant="body1" fontWeight="600">{discharge?.patient?.contact_number || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1" fontWeight="600">{discharge?.patient?.email || 'N/A'}</Typography>
                </Box>
              </Box>
              <Box mt={3}>
                <Typography variant="body2" color="textSecondary" mb={1}>Primary Diagnosis</Typography>
                <Typography variant="body1">{discharge?.primary_diagnosis || 'Not specified'}</Typography>
              </Box>
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" mb={1}>Discharge Summary</Typography>
                <Typography variant="body1">
                  {discharge?.discharge_summary || 'Patient discharged post-stabilization. Requires strict medication adherence and dietary management. Follow-up calls to check for symptom monitoring and education.'}
                </Typography>
              </Box>
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" mb={1}>Discharge Category</Typography>
                <Chip
                  label={discharge?.status || 'Completed'}
                  size="small"
                  sx={{
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    fontWeight: 600
                  }}
                />
              </Box>
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" mb={1}>Follow-up Needed</Typography>
                <Chip
                  label="No"
                  size="small"
                  sx={{
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    fontWeight: 600
                  }}
                />
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
